import { DisposalRecordFloorUnit, DisposalShowItem } from '@agency/apis/disposals';
import { t } from '@core/i18n';
import { DisposalInterestIndexItem } from '@shared/apis/disposals';
import { Option } from '@shared/components/select-new/Select';
import { AvailableSpace } from '@shared/models/space';
import { LettingTransactionTypes } from '@shared/models/transactions/lettings/lettings';
import { InterestScheduleStatuses, ScheduleMatchBaseConfig } from '@shared/types/common/match';
import { getInterestScheduleOrderByConfig, OrderByConfig } from '@shared/utils/interest-schedule';

// Where a match being recorded still sits when it is not on the schedule yet - it only joins the
// schedule once the letting or sale is saved, so closing the drawer leaves it where it was
export type RecordInterestSource = 'inbound' | 'outbound';

export interface PendingRecordInterest {
  option: Option;
  addToSchedule: () => Promise<void>;
}

export const getRecordInterestSource = (status: InterestScheduleStatuses): RecordInterestSource | '' => {
  switch (status) {
    case InterestScheduleStatuses.Inbound:
      return 'inbound';
    case InterestScheduleStatuses.Outbound:
      return 'outbound';
    default:
      return '';
  }
};

// The tenant picker lists matches already on the schedule, so a pending match has to be added to it
export const withPendingInterestOption = (options: Option[], pendingInterest?: PendingRecordInterest): Option[] => {
  if (!pendingInterest || options.some((option) => option.id.toString() === pendingInterest.option.id.toString())) {
    return options;
  }

  return [pendingInterest.option, ...options];
};

// Only the match the drawer was opened for needs adding - the user may since have picked another, or none
export const getPendingInterestBeforeRecord = (pendingInterest: PendingRecordInterest | undefined, matchId: string) =>
  pendingInterest && pendingInterest.option.id.toString() === matchId ? pendingInterest.addToSchedule : undefined;

const sortInterestScheduleByOrder = (
  interestA: DisposalInterestIndexItem,
  interestB: DisposalInterestIndexItem,
  orderBy: OrderByConfig
): number => {
  if (!interestA || !interestB) {
    return 0;
  }
  if (interestA[orderBy.key] === interestB[orderBy.key]) {
    return 0;
  }

  const comparison = interestA[orderBy.key] > interestB[orderBy.key] ? 1 : -1;

  return orderBy.reverse ? -comparison : comparison;
};

export const getInterestScheduleMatchOptions = (
  items: DisposalInterestIndexItem[],
  orderBy: string,
  scheduleMatchConfig?: ScheduleMatchBaseConfig
): Option[] => {
  const options: Option[] = [];

  const order = getInterestScheduleOrderByConfig(orderBy);

  // Order the matches by orderBy then status, furthest ahead first
  const orderedItems = items
    .sort((a, b) => sortInterestScheduleByOrder(a, b, order))
    .sort(function (x, y) {
      return (
        (scheduleMatchConfig?.statuses.indexOf(x.interest_schedule_status) ?? 0) -
        (scheduleMatchConfig?.statuses.indexOf(y.interest_schedule_status) ?? 0)
      );
    });

  // const statusWhitelist = scheduleMatchConfig.groupsActive.filter((status: ScheduleStatusGroups) => {
  //   //TODO Figure out types for groupsActive and statusActionsMap
  //   return scheduleMatchConfig.statusActionsMap[status as unknown as ScheduleStatuses] != ScheduleStatusActions.Complete;
  // });

  orderedItems.forEach((item) => {
    // Ensure status is in whitelist
    // if (statusWhitelist.indexOf(item.interest_schedule_status) === -1) {
    //   return;
    // }

    // Exclude 'discounted' matches
    if (item.interest_schedule_discounted_reason) {
      return;
    }

    // Exclude 'withdrawn' marketplace matches
    if (item.feature_flags.taken_off_marketplace) {
      return;
    }

    const tenantName =
      item.interest_column_values.tenant_name || item.interest_column_values.tenant_contact || t('unknown');
    const agencyName = item.interest_column_values.agent_company || '';
    const agentName = item.interest_column_values.agent_contact || '';

    let label: string;

    if (agencyName && agentName) {
      label = t('xtenantnamex_c_or_o_xagencynamex_bracket_xagentnamex_bracket', {
        tenant_name: tenantName,
        agency_name: agencyName,
        agent_name: agentName,
      });
    } else if (agencyName) {
      label = t('xtenantnamex_c_or_o_xagencynamex', { tenant_name: tenantName, agency_name: agencyName });
    } else if (agentName) {
      label = t('xtenantnamex_c_or_o_bracket_xagentnamex_bracket', { tenant_name: tenantName, agent_name: agentName });
    } else {
      label = tenantName;
    }

    options.push({ id: item.id, label: label.trim() });
  });

  return options;
};

export const convertAvailableSpaceToFloorUnits = (
  spaces: AvailableSpace[],
  propertyId: number,
  prefilledUnit?: number
): DisposalRecordFloorUnit[] => {
  const units: DisposalRecordFloorUnit[] = [];
  spaces.forEach((f_unit) => {
    if (
      f_unit.status === 4 ||
      f_unit.status === 5 ||
      f_unit.status === 6 ||
      f_unit.status === 7 ||
      f_unit.status === 8
    ) {
      return;
    }

    const shouldInclude = prefilledUnit ? (f_unit.id === prefilledUnit ? 1 : 0) : 1;

    const propertyTypes = f_unit.content.building_types ? [f_unit.content.building_types] : [];

    units.push({
      fitout_condition: '',
      floor_string: f_unit.floor_string,
      floor_unit_id: null,
      floor: f_unit.floor_identifier,
      id: f_unit.id,
      include: shouldInclude,
      part_floor: f_unit.part_floor,
      property_id: propertyId,
      property_types: propertyTypes,
      size_metric: f_unit.content.size.metric,
      size_value_sqft: f_unit.content.size.value_sqft ?? null,
      size_value: f_unit.content.size.value?.toString() ?? '',
      unit: f_unit.floor_description,
      unit_string: f_unit.floor_description ?? '',
      status: f_unit.status_string,
    });
  });

  return units;
};

// A space picked from its own floor unit is a partial letting or sale, unless it is the only one available
export const getEntirePropertyDefault = (floorUnits: DisposalRecordFloorUnit[], prefilledUnit?: number) => {
  const result: NumberBoolean = prefilledUnit && floorUnits.length > 1 ? 0 : 1;

  return result;
};

const LEASE_TYPE_TO_TRANSACTION_TYPE: Record<string, LettingTransactionTypes> = {
  assignment: LettingTransactionTypes.ASSIGNMENT,
  new: LettingTransactionTypes.NEW_LEASE,
  sublease: LettingTransactionTypes.SUB_LETTING,
  licence: LettingTransactionTypes.LICENSE,
  temporary: LettingTransactionTypes.LICENSE,
};

const DEFAULT_TRANSACTION_TYPE = LettingTransactionTypes.NEW_LEASE;

export const determineTransactionType = (
  floorUnits: DisposalRecordFloorUnit[],
  disposal: DisposalShowItem,
  availableSpaces: AvailableSpace[]
): number => {
  const includedFloorUnits = floorUnits.filter((unit) => unit.include === 1);

  if (includedFloorUnits.length === 0) {
    return getTransactionTypeFromLeaseTypes(disposal.content.lease.type);
  }

  const spaceMap = new Map(availableSpaces.map((s) => [s.id, s]));

  const spaceLeaseTypes = includedFloorUnits
    .flatMap((unit) => {
      if (unit.id === null) return [];
      const space = spaceMap.get(unit.id);
      return space?.content.lease.type ?? [];
    })
    .filter((leaseType): leaseType is string => Boolean(leaseType) && typeof leaseType === 'string');

  if (spaceLeaseTypes.length === 0) {
    return getTransactionTypeFromLeaseTypes(disposal.content.lease.type);
  }

  const uniqueLeaseTypes = Array.from(new Set(spaceLeaseTypes));
  if (uniqueLeaseTypes.length === 1) {
    return getTransactionTypeFromLeaseTypes(uniqueLeaseTypes);
  }

  if (!disposal.content.lease.type || disposal.content.lease.type.length === 0) {
    return getTransactionTypeFromLeaseTypes([spaceLeaseTypes[0]!]);
  }

  return getTransactionTypeFromLeaseTypes(disposal.content.lease.type);
};

const getTransactionTypeFromLeaseTypes = (leaseTypes: string[] | null | undefined): number => {
  if (!leaseTypes || leaseTypes.length === 0) {
    return DEFAULT_TRANSACTION_TYPE;
  }

  const firstLeaseType = leaseTypes[0];
  if (!firstLeaseType) {
    return DEFAULT_TRANSACTION_TYPE;
  }

  return LEASE_TYPE_TO_TRANSACTION_TYPE[firstLeaseType] ?? DEFAULT_TRANSACTION_TYPE;
};

interface SyncBuildingTypesFloorUnit {
  include: NumberBoolean;
  property_types?: string[];
}

interface SyncBuildingTypesResult {
  finalTypes: string[];
  derivedTypes: string[];
  hasChanges: boolean;
}

export const syncBuildingTypesWithFloorUnits = ({
  floorUnits = [],
  currentBuildingTypes = [],
  previouslyDerivedTypes = [],
}: {
  floorUnits: SyncBuildingTypesFloorUnit[];
  currentBuildingTypes: string[];
  previouslyDerivedTypes: string[];
}): SyncBuildingTypesResult => {
  const floorUnitPropertyTypes = floorUnits
    .filter((unit) => unit.include === 1)
    .flatMap((unit) => unit.property_types || [])
    .filter(Boolean);

  const derivedTypes: string[] = [...new Set(floorUnitPropertyTypes)];

  const typesToRemove = previouslyDerivedTypes.filter((t) => !derivedTypes.includes(t));
  const afterRemoval = currentBuildingTypes.filter((t) => !typesToRemove.includes(t));

  const finalTypes: string[] = [...new Set([...afterRemoval, ...derivedTypes])];

  const hasChanges = JSON.stringify([...finalTypes].sort()) !== JSON.stringify([...currentBuildingTypes].sort());

  return { finalTypes, derivedTypes, hasChanges };
};
